// browsers will change unencoded "\" to "/" which messes up state
const backslash = '%5C';

export interface S {
  value?: string;
  kids?: S[];
}

// Syntax:
//   A(B:C(D))E(F):G::H:\(I\)
// Produces
//   A
//   -B
//   -C
//   --D
//   E
//   -F
//   G
//   H
//
// - Empty values are legal. E.g., "A:(B)" produces
//   "A"
//   -""
//   --"B"
// - Colons separate items.
// - Parens denote children.
// - Colon is optional after closing paren.
// - Escape (:) with \ (\x, where 'x' is non-special, is treated as just 'x').
export function parseSExpr(text: string): S[] {
  const root: S = {};
  _parseSExpr(root, text);
  return root.kids ?? [];
}

function _parseSExpr(parent: S, text: string): number {
  parent.kids = [];

  const push = (): S => {
    const s = { value };
    parent.kids!.push(s);
    value = '';
    return s;
  };

  let value = '';
  let i = 0;
  for (; i < text.length; ++i) {
    const ch = text[i];
    switch (ch) {
      case '(': {
        const s = push();
        i += _parseSExpr(s, text.substring(i + 1));
        break;
      }

      case ')': {
        if (value) {
          push();
        }

        // Consume the ')'.
        i++;

        // If a ':' immediately follows the ')', consume that too, rather than
        // allowing it to generate an empty value.
        if (i < text.length && text[i] === ':') {
          i++;
        }

        return i;
      }

      case ':': {
        push();
        break;
      }

      case '%': {
        // might be the backslash escape
        if (text.substr(i, backslash.length) == backslash) {
          i += backslash.length;
          value += text[i];
        } else {
          value += ch;
        }
        break;
      }

      case '\\': {
        value += text[++i];
        break;
      }

      default: {
        value += ch;
        break;
      }
    }
  }

  if (text[text.length - 1] === ':' || value) {
    push();
  }

  return i;
}

export function emitSExpr(ss: S[]): string {
  let buf = '';
  for (let i = 0; i < ss.length; ++i) {
    const s = ss[i];
    if (s.value) {
      buf += s.value
        .replace(/\\/, backslash + backslash)
        .replace(/\(/, `${backslash}(`)
        .replace(/\)/, `${backslash})`)
        .replace(/:/, `${backslash}:`); // escape chars
    }
    if (s.kids) {
      buf += `(${emitSExpr(s.kids)})`;
    }
    if (i < ss.length - 1) {
      buf += ':';
    }
  }
  return buf;
}
